
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertCircle, CheckCircle2, Clock, XCircle, Bug, RefreshCw, Zap, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

interface WebhookLog {
  id: string;
  user_id: string;
  event_type: string;
  data: any;
  status: string;
  error: string | null;
  created_at: string;
}

interface WebhookConfig {
  id: string;
  user_id: string;
  webhook_url: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const WebhookDebugger: React.FC = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [webhookLogs, setWebhookLogs] = useState<WebhookLog[]>([]);
  const [webhookConfig, setWebhookConfig] = useState<WebhookConfig | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isProcessingWebhooks, setIsProcessingWebhooks] = useState(false);
  const [isSearchingActivationLogs, setIsSearchingActivationLogs] = useState(false);

  const loadWebhookData = async () => {
    if (!user?.id) return;

    setIsRefreshing(true);
    
    try {
      // Buscar configuração de webhook
      const { data: config, error: configError } = await supabase
        .from('webhook_configurations')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (configError) {
        toast.error('Erro ao buscar configuração de webhook');
        return;
      }

      setWebhookConfig(config);

      // Buscar logs de webhook (últimos 20 para mais context)
      const { data: logs, error: logsError } = await supabase
        .from('webhook_logs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (logsError) {
        toast.error('Erro ao buscar logs de webhook');
        return;
      }

      setWebhookLogs(logs || []);

    } catch (error) {
      toast.error('Erro ao carregar dados de webhook');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Nova função para buscar especificamente logs de ativação
  const searchActivationLogs = async () => {
    if (!user?.id) return;

    setIsSearchingActivationLogs(true);
    
    try {
      
      // Buscar logs específicos de ativação dos últimos 30 minutos
      const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000).toISOString();
      
      const { data: activationLogs, error } = await supabase
        .from('webhook_logs')
        .select('*')
        .eq('user_id', user.id)
        .eq('event_type', 'campaign.activated')
        .gte('created_at', thirtyMinutesAgo)
        .order('created_at', { ascending: false });

      if (error) {
        toast.error('Erro ao buscar logs de ativação');
        return;
      }
      
      if (activationLogs && activationLogs.length > 0) {
        toast.success(`Encontrados ${activationLogs.length} logs de ativação recentes`);
        // Atualizar a lista com os logs encontrados
        await loadWebhookData();
      } else {
        toast.info('Nenhum log de ativação encontrado nos últimos 30 minutos');
      }

    } catch (error) {
      toast.error('Erro ao buscar logs de ativação');
    } finally {
      setIsSearchingActivationLogs(false);
    }
  };

  React.useEffect(() => {
    loadWebhookData();
  }, [user?.id]);

  const testWebhookFunction = async () => {
    setIsLoading(true);
    
    try {
      
      const { data, error } = await supabase.functions.invoke('campaign-automation-webhook', {
        method: 'POST',
        body: { manual_test: true }
      });

      if (error) {
        toast.error(`Erro ao testar função de webhook: ${error.message}`);
        return;
      }
      toast.success('Teste da função de webhook concluído! Verifique os logs.');
      
      // Recarregar dados após o teste
      setTimeout(() => {
        loadWebhookData();
      }, 2000);

    } catch (error) {
      toast.error('Erro ao executar teste de webhook');
    } finally {
      setIsLoading(false);
    }
  };

  const processAllPendingWebhooks = async () => {
    setIsProcessingWebhooks(true);
    
    try {
      
      const { data, error } = await supabase.functions.invoke('campaign-automation-webhook', {
        method: 'POST',
        body: { process_all: true }
      });

      if (error) {
        toast.error(`Erro ao processar webhooks: ${error.message}`);
        return;
      }
      toast.success(`Processamento concluído! ${data?.processed || 0} webhooks processados.`);
      
      // Recarregar dados após o processamento
      setTimeout(() => {
        loadWebhookData();
      }, 2000);

    } catch (error) {
      toast.error('Erro ao processar webhooks');
    } finally {
      setIsProcessingWebhooks(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge variant="default" className="bg-green-100 text-green-800"><CheckCircle2 className="h-3 w-3 mr-1" />Sucesso</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Falha</Badge>;
      case 'pending':
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Pendente</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const pendingWebhooks = webhookLogs.filter(log => log.status === 'pending');
  const activationLogs = webhookLogs.filter(log => log.event_type === 'campaign.activated');

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5" />
            Debug de Webhook - Reativação de Campanha
          </CardTitle>
          <CardDescription>
            Diagnóstico específico para webhook de reativação de campanhas e teste do sistema geral.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2 flex-wrap">
            <Button 
              onClick={loadWebhookData}
              disabled={isRefreshing}
              variant="outline"
              size="sm"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Atualizar Dados
            </Button>
            
            <Button 
              onClick={searchActivationLogs}
              disabled={isSearchingActivationLogs}
              variant="outline"
              size="sm"
              className="bg-blue-50 hover:bg-blue-100"
            >
              <Search className={`h-4 w-4 mr-2 ${isSearchingActivationLogs ? 'animate-spin' : ''}`} />
              {isSearchingActivationLogs ? 'Buscando...' : 'Buscar Logs de Ativação'}
            </Button>
            
            <Button 
              onClick={testWebhookFunction}
              disabled={isLoading}
              size="sm"
              variant="outline"
            >
              <Bug className="h-4 w-4 mr-2" />
              {isLoading ? 'Testando...' : 'Testar Função'}
            </Button>

            {pendingWebhooks.length > 0 && (
              <Button 
                onClick={processAllPendingWebhooks}
                disabled={isProcessingWebhooks}
                size="sm"
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Zap className="h-4 w-4 mr-2" />
                {isProcessingWebhooks ? 'Processando...' : `Processar ${pendingWebhooks.length} Pendentes`}
              </Button>
            )}
          </div>

          {/* Status da Configuração */}
          <div className="border rounded-lg p-4">
            <h4 className="font-medium mb-2">Configuração de Webhook</h4>
            {webhookConfig ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Configuração encontrada</span>
                </div>
                <div className="text-sm text-gray-600">
                  <p><strong>URL:</strong> {webhookConfig.webhook_url}</p>
                  <p><strong>Ativo:</strong> {webhookConfig.is_active ? 'Sim' : 'Não'}</p>
                  <p><strong>Criado em:</strong> {new Date(webhookConfig.created_at).toLocaleString('pt-BR')}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-orange-600" />
                <span className="text-sm">Nenhuma configuração de webhook encontrada</span>
              </div>
            )}
          </div>

          {/* Alertas sobre webhooks pendentes */}
          {pendingWebhooks.length > 0 && (
            <div className="border-orange-200 bg-orange-50 border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-4 w-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-800">
                  {pendingWebhooks.length} webhook(s) pendente(s) encontrado(s)
                </span>
              </div>
              <p className="text-sm text-orange-700">
                Estes webhooks não foram enviados ainda. Clique em "Processar Pendentes" para enviá-los agora.
              </p>
            </div>
          )}

          {/* Status específico de logs de ativação */}
          {activationLogs.length > 0 && (
            <div className="border-blue-200 bg-blue-50 border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  {activationLogs.length} log(s) de ativação encontrado(s)
                </span>
              </div>
              <p className="text-sm text-blue-700">
                Logs de webhook para eventos de ativação de campanha foram encontrados.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Logs de Webhook (Últimos 20)</CardTitle>
          <CardDescription>
            Histórico de tentativas de webhook para suas campanhas, incluindo ativações.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {webhookLogs.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Evento</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Campanha</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Erro</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {webhookLogs.map((log) => (
                  <TableRow key={log.id} className={log.event_type === 'campaign.activated' ? 'bg-blue-50' : ''}>
                    <TableCell className="text-sm">
                      {new Date(log.created_at).toLocaleString('pt-BR')}
                    </TableCell>
                    <TableCell className="text-sm">
                      <Badge variant={log.event_type === 'campaign.activated' ? 'default' : 'secondary'}>
                        {log.event_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(log.status)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {log.data?.data?.campaign_name || 'N/A'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {log.data?.webhook_url ? (
                        <a 
                          href={log.data.webhook_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline truncate max-w-xs block"
                        >
                          {log.data.webhook_url}
                        </a>
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {log.error && (
                        <div className="text-red-600 max-w-xs truncate" title={log.error}>
                          {log.error}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Nenhum log de webhook encontrado.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Debug Info */}
      <Card>
        <CardHeader>
          <CardTitle>Informações de Debug</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <p><strong>User ID:</strong> {user?.id}</p>
            <p><strong>Webhook Config:</strong> {webhookConfig ? 'Configurado' : 'Não configurado'}</p>
            <p><strong>Total de Logs:</strong> {webhookLogs.length}</p>
            <p><strong>Webhooks Pendentes:</strong> {pendingWebhooks.length}</p>
            <p><strong>Logs de Ativação:</strong> {activationLogs.length}</p>
            <p><strong>Último Log:</strong> {webhookLogs[0] ? new Date(webhookLogs[0].created_at).toLocaleString('pt-BR') : 'Nenhum'}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
